import { gql } from '@apollo/client';
import type { MeResponse, CompanyInfo } from '@scio3/shared-types';

/** Apollo query result type for ME_QUERY */
export interface MeQueryResult {
  me: MeResponse | null;
}

/** Apollo query result type for GET_COMPANY_INFO */
export interface GetCompanyInfoQueryResult {
  getCompanyInfo: CompanyInfo | null;
}

export const ME_QUERY = gql`
  query Me {
    me {
      _id
      accountId
      username
      email
      firstName
      lastName
      name
      language
      rootRole
      teams {
        _id
        name
        role
        permissions
        isTeamBilling
      }
      feature
      featureData
      brandingData
      planFeatures {
        _id
        name
        features {
          name
          description
          enable
          checked
          isHide
        }
      }
      payment
      accountInfo
      userSettings
      isSuperUser
      isGuestUser
      ownerEmail
      ownerName
      passwordChangeRequire
      accountStatus
      fileCdnkeyData
      scioVersion
      appMetas {
        _id
        appType
        text
        appDescription
        image
        iconClass
        customClass
        customIcon
        thumbnail
        thumbnailBackgroundPrimaryColor
        order
        tags
        appCategories
        categories
        isPopular
        isLocked
        isFree
        minPlanRequired
        supportedPlans
        promotedIcon
        promoted
        engage
        showWithFeature
        disableCreateAsset
        allowIframe
        allowSmallAppIframe
        canAssignPlaylist
        canPreview
        previewMode
        canDownload
        canClone
        canExpire
        canMoveTo
        copyAssetId
        isOpenModalPreview
        scrollByParent
        signatureRequired
        func
        remoteName
        backendUrl
        apiUrl
        frontendUrl
        hasManifest
        authTokenRequired
        rootAppType
        isGetDetail
        appContent
      }
      appMetaGroups {
        _id
        title
        description
        icon
        appTypes
        order
        isShowWelcome
        orderWelcome
        isFree
        isSkipSelectApp
        settings
        appContent
      }
      inviteUser
    }
  }
`;

export const GET_COMPANY_INFO = gql`
  query GetCompanyInfo {
    getCompanyInfo {
      _id
      name
      industry
      payment {
        planFeature
        trialEndDate
        isTrial
        isActive
        trialBanner
        plan
        nextCharge
        billing_email
        subscriptionIdAddOns
        scheduleCancel
        currency
        firstScreenAssignmentEventFired
        firstDevicePaired
        firstContentCreated
        firstPlaylistCreated
        subscription_freedevice_id
        isSyncPriceStripe
      }
      limitation {
        maxQuantity
        quantity
        maxStorage
        usedStorage
        maxCustomFonts
        disableDownload
        maxQuantityFreeDevice
        customerCanNotChangeSub
      }
      card {
        brand
        last4
        type
        exp_month
        exp_year
        paymentMethodId
        address_country
        address_zip
        address_city
        address_state
      }
      feature {
        isEnterprise
        enterpriseAPP
        disablePaymentCheck
        allowTeams
        samlSSO
        roleSecurity
        folderLevelSecurity
        branding
        remoteControl
        remoteCommand
        secureLink
        lockdownDevice
        proofOfPlay
        ai
        apiKey
        apiAccessToken
        engage
        approvalWorkflow
        securityAuditLogging
        customBrandCanva
        alertPolicy
        externalCom
        comLog
        shareLink
        totpMfa
        phoneMfa
        contentPlaylistTag
        subPlaylistControl
        advancedSchedule
        cecSchedule
        advancedDeviceManagement
        sensor
        playEvery
        resumeOnNextPlay
        feedsCreate
        feedsManage
        feedsCategories
        enforcePasswordComplexity
        # Designer & content
        enforceBranding
        lockableObjectDesigner
        designerOpenAIText
        displayFormat
        uploadFont
        dynamicDataMapping
        # Apps & integrations
        allowPowerBI
        powerbiServicePrincipal
        hostedWebsite
        webScripting
        liveTvAds
        securedDashboardApp
        # Device management
        utilsMDM
        mdm
        massProvision
        optiSignsScreenCapture
        # API & gateway
        apiUploadFiles
        # Emergency & alerts
        CAPAlert
        emergencyFeed
        # Proof of play
        enableHourlyPOPExtract
        enablePOPExtractPriority
        # Preferences (also in GET_COMPANY_PREFERENCES)
        wifiFallback
        enforceSSO
        disableVideoConversion
        # Infrastructure / Data Residency
        bucket
        disableS3
        disableDOSpaces
        aericast
        smallappManifest
        # Additional features
        feeds
        apiGateway
        allowLooker
        primaryZone
        teamBilling
        scriptInjection
        interactiveKiosk
        orderManagedDevice
        msAppOauthBrandPortal
        disableLimitUploadResolutionImage
        virtualScreenCountLimit
        enableContentAIDetection
        disableAIBrandPortal
        openAIApiKey
        # Free-device plan gating (SCIO 1.0 subscriptionPlanModal.js isDisablePlan)
        enableYearlyFreeDevice
        allowAddYearlyFreeDevice
        allowAddYearly10Plan
        allowAddMonthlyPlan
        scim
        # International ordering — suppresses the non-US shipping warning
        # dialog on Order Device when true (SCIO 1.0 orderDeviceComponent.js:1112).
        allowIntlOrder
      }
      featureData {
        language
        defaultPLDuration
        defaultPLTransition
        defaultPLScaleImage
        defaultPLScaleVideo
        defaultPLScaleDocument
        targetTagsDefault
        defaultNavIconPosition
        defaultNavIconStyle
        defaultNavIconColorType
        defaultNavIconColorCode
        defaultNavIconSizeType
        defaultNavIconSize
        defaultNavBackgroundColor
        defaultNavOpacity
        isSecurityAuditLogging
        auditLogsAllowIp
        dateFormat
        timeFormat
        numberFormat
        scaleTemperature
        windSpeedUnit
        defaultValueType
        enableSubAccount
        isRemoteCommand
        secureLink
        comLogStatus
        comLogRealTime
        comLogInterval
        comLogType
        comLogWeek
        comLogMonth
        comLogRunWindows
        comLogTimezone
        comLogEmail
        comLogEmailFrom
        comLogAWSS3
        comLogAWSS3Bucket
        comLogAWSS3BucketPath
        comLogAWSS3Region
        proofOfPlayStatus
        proofOfPlayRealTime
        proofOfPlayInterval
        proofOfPlayType
        proofOfPlayWeek
        proofOfPlayMonth
        proofOfPlayRunWindows
        proofOfPlayTimezone
        proofOfPlayEmail
        proofOfPlayEmailFrom
        proofOfPlayAWSS3
        proofOfPlayAWSS3Bucket
        proofOfPlayAWSS3BucketPath
        proofOfPlayAWSS3Region
      }
      getStarted {
        triggered
        completed
        completedDate
      }
      optidev {
        workspaceId
      }
      createdAt
      noOfScreens
      scioVersion
    }
  }
`;

export const GET_USER_SETTINGS_AUTH = gql`
  query GetUserSettingsAuth {
    getUserSettingsAuth {
      _id
      isShowWelcomeTips
      isShowOrderAlertStick
      onboardFormSkip
      onboardFormSubmitted
      firstGetStarted
      firstGetStartedDate
      themeMode
      dateFormat
      timeFormat
      numberFormat
      scaleTemperature
      windSpeedUnit
      defaultValueType
      assetIsDupClickMode
      isHideBannerPromotionForPaidUser
    }
  }
`;

export const FIND_SSO_LOGIN = gql`
  query findSSOLogin($email: String!) {
    findSSOLogin(email: $email)
  }
`;

export const GENERATE_SIGN_IN_WITH_EMAIL_LINK = gql`
  mutation generateSignInWithEmailLink($email: String!) {
    generateSignInWithEmailLink(email: $email)
  }
`;

export const REGISTER_USER = gql`
  mutation register($payload: SignUpUserInput) {
    register(payload: $payload)
  }
`;

/** Check if an invited email's company enforces SSO. Returns provider name or null. */
export const CHECK_ENFORCE_SSO = gql`
  query CheckEnforceSSO($email: String!) {
    checkEnforceSSO(email: $email)
  }
`;

/** Check if a MongoDB user exists for a given email (used on signup to distinguish Firebase-only vs OptiSigns user). */
export const CHECK_USER_EXISTS = gql`
  query CheckUserExists($email: String!) {
    checkUserExists(email: $email)
  }
`;

export const TEAMS_QUERY = gql`
  query Teams($first: Int, $sort: TeamSort) {
    teams(first: $first, sort: $sort) {
      page {
        hasNextPage
        endCursor
      }
      edges {
        node {
          _id
          name
          description
          limitation {
            maxQuantity
            quantity
          }
        }
      }
    }
  }
`;

/** Result type for ACCEPT_TEAM_INVITE_TOKEN mutation */
export interface AcceptTeamInviteTokenResult {
  acceptTeamInviteToken: { tokenId: string; userId: string } | null;
}

/** Variables type for ACCEPT_TEAM_INVITE_TOKEN mutation */
export interface AcceptTeamInviteTokenVariables {
  token: string;
}

/**
 * Accept team invite via SSO token.
 * Returns tokenId that can be used with getCustomLoginToken to get Firebase custom token.
 */
export const ACCEPT_TEAM_INVITE_TOKEN = gql`
  mutation AcceptTeamInviteToken($token: String!) {
    acceptTeamInviteToken(token: $token)
  }
`;

/** Result type for RESET_PASSWORD mutation */
export interface ResetPasswordResult {
  resetPassword: boolean;
}

/** Variables type for RESET_PASSWORD mutation */
export interface ResetPasswordVariables {
  email: string;
}

/**
 * Request password reset email via Customer.io.
 * Backend generates Firebase oobCode and sends branded email with URL:
 * {ROOT_URL}password/reset?oobCode=xxx&accountId=yyy&c=z
 *
 * - oobCode: Firebase action code for password reset
 * - accountId: User's account ID (for branding lookup)
 * - c: Complexity flag (1 = enforce complex password rules)
 */
export const RESET_PASSWORD = gql`
  mutation ResetPassword($email: String!) {
    resetPassword(email: $email)
  }
`;

/** Result type for GET_PASSWORD_RESET_COMPLEXITY query */
export interface GetPasswordResetComplexityResult {
  getPasswordResetComplexity: boolean;
}

/** Variables type for GET_PASSWORD_RESET_COMPLEXITY query */
export interface GetPasswordResetComplexityVariables {
  oobCode: string;
}

/**
 * Get CURRENT password complexity setting for password reset page.
 * Uses Firebase oobCode to look up user → company → enforcePasswordComplexity flag.
 * Backend verifies oobCode via Firebase Admin SDK to extract user email.
 */
export const GET_PASSWORD_RESET_COMPLEXITY = gql`
  query GetPasswordResetComplexity($oobCode: String!) {
    getPasswordResetComplexity(oobCode: $oobCode)
  }
`;

/** Result type for CONFIRM_PASSWORD_RESET mutation */
export interface ConfirmPasswordResetResult {
  confirmPasswordReset: boolean;
}

/** Variables type for CONFIRM_PASSWORD_RESET mutation */
export interface ConfirmPasswordResetVariables {
  oobCode: string;
}

/**
 * Clear the password reset oobCode from user record after successful reset.
 * Called after Firebase confirmPasswordReset succeeds to clean up backend state.
 */
export const CONFIRM_PASSWORD_RESET = gql`
  mutation ConfirmPasswordReset($oobCode: String!) {
    confirmPasswordReset(oobCode: $oobCode)
  }
`;

/** Result type for SWITCH_ACCOUNT_MEMBER_ACTION mutation */
export interface SwitchAccountMemberActionResult {
  switchAccountMemberAction: boolean;
}

/** Variables type for SWITCH_ACCOUNT_MEMBER_ACTION mutation */
export interface SwitchAccountMemberActionVariables {
  payload: { inviteAccepted: boolean };
}

/**
 * Accept or reject an account switch invitation.
 * When inviteAccepted=true, user moves to the new account (loses access to current).
 * When inviteAccepted=false, invitation is rejected.
 * Both cases require signOut + reload after mutation completes.
 */
export const SWITCH_ACCOUNT_MEMBER_ACTION = gql`
  mutation SwitchAccountMemberAction($payload: SwitchAccountMemberActionInput!) {
    switchAccountMemberAction(payload: $payload)
  }
`;

export const UPDATE_PASSWORD_CHANGE_REQUIRE = gql`
  mutation UpdatePasswordChangeRequire {
    updatePasswordChangeRequire
  }
`;
